/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Box, Button, Grid, Typography, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { resetTempUserInfo } from '../userSlice';
import { reset } from '../habitationSlice';
import { attestationContract } from '@/api/stripeApi';
import { appError, appLoading, appSuccess } from '@/redux/actions/appActions';
import { authSuccess } from '@/redux/actions/authActions';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InfoText from '../components/InfoText';
import { getDocument } from '@/redux/actions/contractsActions';
import { getMe, createEmailConfirmationStream } from '@/api/authApi';
import type { EmailConfirmationMessage } from '../types';
import LocalStorageService from '@/services/LocalStorageService';

const Marge = styled.div`
  height: 150px;
`;

const Spacing = styled.div`
  height: 80px;
`;

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 200px;
  height: 40px;
  font-weight: 400;
  font-size: 12px;
  &:hover {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
    width: 100%;
    font-size: 13px;
    height: 30px;
  }
`;

const SuccessSignaturePage: React.FC = () => {
  const { t } = useTranslation('habitation');
  const dispatch = useAppDispatch();
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  // Extract contractReference once to avoid recreating query object
  const contractReference = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('contractReference');
  }, []); // Empty deps = only once on mount

  const [attestationId, setAttestationId] = useState<number | null>(null);
  const [checkingValidation, setCheckingValidation] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);
  const hasCalledAttestationRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Fermer la connexion SSE
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!contractReference) return;

    // Empêcher les appels multiples pour les utilisateurs non-temporaires
    if (!isTempRegistered && hasCalledAttestationRef.current) {
      return;
    }

    dispatch(appLoading());

    if (!isTempRegistered) {
      hasCalledAttestationRef.current = true;
      attestationContract(contractReference)
        .then(res => {
          if (!isMountedRef.current) return;

          dispatch(appSuccess());
          if (res.data?.attestationId) {
            setAttestationId(res.data.attestationId);
          }
          // Clean up localStorage after successful signature
          localStorage.removeItem('habitation_contractReference');
          dispatch(reset());
          dispatch(resetTempUserInfo());
        })
        .catch(err => {
          if (!isMountedRef.current) return;

          dispatch(appError());
          console.error('Erreur récupération lien de signature :', err);
        });
      return;
    }

    dispatch(reset());
    dispatch(appSuccess());
    setCheckingValidation(true);

    // 🚀 SSE : Connexion temps réel pour attendre la validation email
    if (!contractReference) {
      console.error('❌ Pas de contractReference trouvée');
      return;
    }

    eventSourceRef.current = createEmailConfirmationStream(contractReference, {
      onOpen: () => {
        // SSE connection established
      },

      onMessage: (data: EmailConfirmationMessage) => {
        if (!isMountedRef.current) return;

        // Quand l'email est confirmé
        if (data.status === 'confirmed' && data.active && data.isVerified) {

          // Fermer la connexion SSE proprement
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }

          localStorage.removeItem('accountValidated');
          dispatch(resetTempUserInfo());

          // Mettre à jour state.auth et localStorage pour que l'utilisateur soit considéré comme connecté globalement
          getMe()
            .then(response => {
              if (!isMountedRef.current) return;

              if (response?.data) {
                dispatch(authSuccess({
                  data: { user: response.data },
                  token: null
                }));
                // Sync localStorage for isAuthenticated() and global app state
                const service = LocalStorageService.getService();
                service.setUser({ user: response.data });
              }
            })
            .catch(err => {
              console.error('Erreur lors de la mise à jour auth:', err);
            });

          // Mettre à jour le state au lieu de recharger la page
          setCheckingValidation(false);
          setEmailValidated(true);

          // Récupérer l'attestation après validation
          attestationContract(contractReference)
            .then(res => {
              if (!isMountedRef.current) return;

              if (res.data?.attestationId) {
                setAttestationId(res.data.attestationId);
              }
              // Clean up localStorage after successful signature
              localStorage.removeItem('habitation_contractReference');
              dispatch(reset());
            })
            .catch(err => {
              console.error('Erreur récupération attestation :', err);
              dispatch(appError());
            });
        }
      },

      onError: () => {
        // Fermer et nettoyer en cas d'erreur
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Fallback sur un check manuel si SSE échoue
        console.warn('⚠️ SSE échoué, tentative de vérification manuelle...');
        setTimeout(async () => {
          if (!isMountedRef.current) return;

          try {
            const res = await getMe();
            if (res.status === 200 && res.data?.active && res.data?.isVerified) {
              localStorage.removeItem('accountValidated');
              dispatch(resetTempUserInfo());
              window.location.href = window.location.pathname + window.location.search;
            }
          } catch (err) {
            console.error('Erreur check manuel:', err);
          }
        }, 2000);
      },
    });
  }, [contractReference, isTempRegistered, dispatch]);

  const handleClick = (id: number) => {
    dispatch(getDocument({ id, filename: 'attestation.pdf' }));
  };

  return (
    <>
      <Marge />
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 2, mt: 2, position: 'relative' }}>
        {/* Bouton retour à l'accueil en haut à droite */}
        {(!isTempRegistered || emailValidated) && (
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = '/home'}
            sx={{
              borderColor: '#2f6975',
              color: '#2f6975',
              '&:hover': {
                borderColor: '#26555f',
                backgroundColor: 'rgba(47, 105, 117, 0.04)',
              },
            }}
          >
            {t('stepfinal.back_to_home')}
          </Button>
        </Box>
        )}
        <Grid container>
          <Grid item xs={12} sm={12} textAlign="center">
            <Box
              component="img"
              src="/images/logos/logo-blue.png"
              alt="logo"
              sx={{
                height: { xs: 64, sm: 90 },
                width: { xs: 42, sm: 52 },
                mx: { xs: 'auto', sm: 0 },
              }}
            />
          </Grid>
          <Spacing />
          <Grid item xs={12} sm={12} textAlign="center">
            <Typography variant="h3">
              {t('stepfinal.subscribe_success')}
            </Typography>
          </Grid>
          
          {/* Afficher les remerciements si pas d'inscription temporaire OU si email validé */}
          {(!isTempRegistered || emailValidated) && (
            <>
              <Spacing />
              <Grid item xs={12} sm={12} textAlign="center">
                <Typography variant="h1">
                  {t('stepfinal.subscribe_thanks')}
                </Typography>
              </Grid>
              <Spacing />
            </>
          )}

          {/* Afficher le loader uniquement si inscription temporaire ET en attente de validation */}
          {isTempRegistered && checkingValidation && !emailValidated && (
            <Grid item xs={12} sm={12} textAlign="center" mt={4}>
              <CircularProgress color="primary" />
              <Box mt={2}>
                <Typography variant="h5">
                  {t('stepfinal.waiting_validation')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('stepfinal.email_sent_check')}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Afficher l'attestation si disponible (utilisateur connecté OU email validé) */}
          {(!isTempRegistered || emailValidated) && attestationId && (
            <Grid item xs={12} sm={12} textAlign="center" mt={2}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  background: '#e3f2fd',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mt: 2,
                  boxShadow: 1,
                }}
              >
                <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />
                <ButtonStyled
                  type="button"
                  onClick={() => handleClick(attestationId)}
                  style={{
                    fontWeight: 600,
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  aria-label={t('stepfinal.download_attestation')}
                >
                  {t('stepfinal.download_attestation')}
                </ButtonStyled>
              </Box>
              <InfoText>{t('stepfinal.download_attestation_hint')}</InfoText>
            </Grid>
          )}
        </Grid>
      </Box>
      <Marge />
    </>
  );
};

export default SuccessSignaturePage;